import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button.jsx'; // Pagination buttons
import { Skeleton } from "../../components/ui/skeleton.jsx";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.jsx";
import { useToast } from "../../hooks/use-toast.js";
import { DateTime } from 'luxon';
import axios from '../../lib/axios.js';
import { TabsContent } from '@/components/ui/tabs.jsx';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

const Emails = ({ community }) => {
    const [emailData, setEmailData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalEmails, setTotalEmails] = useState(0);
    const [limit] = useState(10); // Number of items per page
    const { toast } = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchEmailData = async () => {
            try {
                setIsLoading(true);
                const response = await axios.get(`/communities/${community.id}/emails?page=${page}&limit=${limit}`);
                const { data } = response;
                setEmailData(data.data);
                setTotalEmails(data.total);
            } catch (error) {
                console.error('Error fetching email data:', error);
                toast({
                    title: "Error",
                    description: "Failed to load email data. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchEmailData();
    }, [community, page, limit, toast]);

    const formatDate = (date) => {
        const dt = DateTime.fromISO(date).setZone(community.time_zone);
        const currentYear = DateTime.now().setZone(community.time_zone).year;

        // Choose format string based on whether the date is in the current year
        const fmt = dt.year === currentYear
            ? 'MMM d, hh:mm a'        // e.g., “May 05, 09:00 AM”
            : 'MMM d, yyyy hh:mm a';  // e.g., “May 05, 2024 09:00 AM”

        return dt.toFormat(fmt);
    };

    const totalPages = Math.ceil(totalEmails / limit);

    const handlePreviousPage = () => {
        if (page > 1) setPage(page - 1);
    };

    const handleNextPage = () => {
        if (page < totalPages) setPage(page + 1);
    };

    if (!isLoading && (!emailData || emailData.length === 0)) {
        return <TabsContent value="emails">
            <div className="text-center text-gray-500">No email data available</div>
        </TabsContent>
    }

    return (
        <TabsContent value="emails">
            <div className="rounded-lg border border-gray-200 overflow-hidden">
                <Table className="rounded-lg">
                    <TableHeader className="bg-gray-100 rounded-lg">

                        <TableRow className="rounded-lg">
                            <TableHead>Subject</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>From</TableHead>
                            <TableHead>To</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {!!isLoading &&
                            Array.from({ length: 10 }, (_, i) => i).map((i) =>
                                <TableRow key={i}>
                                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                                </TableRow>
                            )
                        }
                        {!isLoading && emailData.map((email) => (
                            <TableRow key={email.id} onClick={() => navigate(`/emails/${email.id}`)}>
                                <TableCell>{email.subject}</TableCell>
                                <TableCell>{formatDate(email.received_at)}</TableCell>
                                <TableCell>{email.from_email}</TableCell>
                                <TableCell>{email.to_email}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination controls */}
            <div className="gap-6 flex justify-end mt-4">
                <Button size="sm" variant="outline" onClick={handlePreviousPage} disabled={page === 1}><ChevronLeftIcon className='w-4 h-4' /></Button>
                <span className='text-grey-500 self-center'>Page {page} of {totalPages}</span>
                <Button size="sm" variant="outline" onClick={handleNextPage} disabled={page === totalPages}><ChevronRightIcon className='w-4 h-4' /> </Button>
            </div>
        </TabsContent>
    );
};

export default Emails;